
/**
 * Module depenencies.
 */

import { Awards } from 'src/types/company-page';
import { Slide } from 'src/components/core/slider';
import { Type, isExternalUrl, media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useRouter } from 'next/router';
import Carousel from 'src/components/core/carousel';
import Container from 'src/components/core/layout/container';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInLeftAnimation from 'src/components/core/animations/fade-in-left';
import Image from 'src/components/core/image';
import React, { Fragment, ReactElement, useRef, useState } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  awards: Awards[],
  canTrigger: boolean,
  className?: string,
  isAnimated: boolean,
  isVisible: boolean,
  title: string
};

/**
 * `Wrapper` component.
 */

const Wrapper = styled.div`
  margin: 0 auto;
  position: relative;

  ${media.min('md')`
    padding-top: 5vw;
  `}

  .swiper-container {
    padding: 0 ${theme('grid.gutterMobile')}px;
    width: 100%;

    ${media.min('lg')`
      padding: 0 0 0 calc(((100vw - ${theme('breakpoints.md')}px) / 2) - ${theme('grid.gutter')}px);
    `}

    ${media.min('xxl')`
      padding-left: calc(((100vw - ${theme('breakpoints.xl')}px) / 2) - ${theme('grid.gutter')}px);
    `}
  }

  .swiper-slide {
    max-width: 300px;
    width: auto;
  }
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas: 'title .';
    grid-template-columns: 6fr 4fr;
  `}
`;

/**
 * `SlideLink` styled component.
 */

const SlideLink = styled.a.attrs(({ href }) => ({
  as: href && !isExternalUrl(href) && RouterLink || 'a'
}))`
  color: inherit;
  cursor: pointer;
  text-decoration: none;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  height: 118px;
  overflow: hidden;
  position: relative;

  ${media.min('ms')`
    width: ${units(30)};
  `}
`;

/**
 * Carousel config.
 */

const carouselConfig = {
  autoplay: false,
  breakpoints: {
    768: {
      slidesPerView: 'auto'
    }
  },
  freemode: true,
  slidesPerView: 1.5
};

/**
 * `AwardsSection` component.
 */

const AwardsSection = (props: Props): ReactElement | null => {
  const { awards, canTrigger, className, isAnimated, isVisible, title } = props;
  const { locale } = useRouter();
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);

  return (
    <Wrapper className={className}>
      <FadeInAnimation
        canTrigger={canTrigger}
        isAnimated={isAnimated}
        isVisible={isVisible}
        options={{
          transitionDelay: 1,
          transitionTime: 1
        }}
      >
        <Container>
          <Grid>
            <Type.H1
              as={'h2'}
              gridAreaMs={'title'}
              marginBottom={units(9)}
              marginBottomMd={units(15)}
              textTransform={'lowercase'}
            >
              {title}
            </Type.H1>
          </Grid>
        </Container>
      </FadeInAnimation>

      <FadeInLeftAnimation
        canTrigger={canTrigger}
        isAnimated={isAnimated}
        isVisible={isVisible}
        options={{
          distance: '100%',
          transitionDelay: 0.5,
          transitionTime: 1
        }}
      >
        <Carousel
          activeSlide={activeSlide}
          carouselConfig={carouselConfig}
          containerModifierClass={'awards-slider'}
          onSetActiveSlide={setActiveSlide}
          ref={sliderRef}
          speed={1000}
        >
          {map(awards, ({ image, url }: Awards, index: number) => {
            const SlideWrapper = !url ? Fragment : SlideLink;
            const slideWrapperProps = url ? {
              href: url,
              ...!isExternalUrl(url) ? { locale } : {
                rel: 'noopener',
                target: '_blank'
              }
            } : {};

            return (
              <Slide key={index}>
                <SlideWrapper {...slideWrapperProps}>
                  <ImageWrapper>
                    <Image
                      alt={title}
                      layout={'fill'}
                      objectFit={'contain'}
                      src={image}
                    />
                  </ImageWrapper>
                </SlideWrapper>
              </Slide>
            );
          })}
        </Carousel>
      </FadeInLeftAnimation>
    </Wrapper>
  );
};

/**
 * Export `AwardsSection` component.
 */

export default AwardsSection;
